import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Icon,
  Text,
  Spinner,
  Center,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Stack
} from '@chakra-ui/react';
import { IoGridOutline } from 'react-icons/io5';
import { ImStatsDots } from 'react-icons/im';
import { AiFillFormatPainter } from 'react-icons/ai';
import { RiAuctionLine } from 'react-icons/ri';
import { ArtistModel, ItemModel } from 'models';
import { useAdminUser } from 'hooks/queries/useAdminUser';
import { usePortfolio, usePortfolioArtists, usePortfolioHistory, usePortfolioListings } from 'hooks/queries/usePortfolio';
import { NavigationService } from 'services';
import {
  TabGroup,
  EmptyState,
  PortfolioTable,
  PaginatedListingGrid,
  ArtistGrid,
  GrowthBadge,
  UserPortfolioBadge
} from 'components';
import PortfolioHistoryChart from 'components/Charts/PortfolioHistoryDataChart';
import ArtistDistributionPieChart from 'components/Charts/ArtistDistributionPieChart';
import TypeDistributionPieChart from 'components/Charts/TypeDistributionPieChart';
import { getSelectedTabFromURL } from 'utils/url';
import { QuerySearchParameters } from 'constants/url';
import { toDollarFormat } from 'utils/format';

export default function PortfolioDetailsView() {
  const { id: portfolioId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [listingPage, setListingPage] = useState(1);

  const { data: portfolio, isLoading: portfolioLoading } = usePortfolio(portfolioId);
  const { data: portfolioArtists, isLoading: portfolioArtistsLoading } = usePortfolioArtists(portfolioId);
  const { data: portfolioHistory, isLoading: portfolioHistoryLoading } = usePortfolioHistory(portfolioId);
  const { data: portfolioListings, isLoading: portfolioListingsLoading } = usePortfolioListings(portfolioId, listingPage);
  const { data: ownerUser, isLoading: userLoading, isError: userError } = useAdminUser(portfolio?.user);

  const initialTab = getSelectedTabFromURL(location);

  useEffect(() => {
    setListingPage(1);
  }, [portfolioId]);

  const detailedPortfolioItems = useMemo(() => {
    return portfolio?.getPortfolioDetailedItems() ?? [];
  }, [portfolio]);

  const portfolioItems: ItemModel[] = useMemo(() => {
    return detailedPortfolioItems.map(item => item.item);
  }, [detailedPortfolioItems]);

  const metrics = useMemo(() => {
    if (!portfolio) {
      return {
        totalItems: 0,
        uniqueArtists: 0,
        cost: 0,
        value: 0,
      };
    }

    const marketSummary = portfolio.getPortfolioBasicMarketSummary();

    return {
      totalItems: detailedPortfolioItems.length,
      uniqueArtists: portfolio.getArtistIds().length,
      cost: marketSummary.cost,
      value: marketSummary.value,
    };
  }, [portfolio, detailedPortfolioItems]);

  const updateTabInURL = (tabIndex: number): void => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(QuerySearchParameters.TAB, tabIndex.toString());
    navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
  };

  const onTabChange = (index: number): void => {
    const currentTab = getSelectedTabFromURL(location);
    if (currentTab !== index) {
      updateTabInURL(index);
    }
  };

  const onItemNameClick = (itemId: string, itemName: string) => {
    navigate(NavigationService.getItemDetailsPath(itemId, itemName));
  };

  const onItemArtistClick = (artistId: string, artistName: string) => {
    navigate(NavigationService.getArtistDetailsPath(artistId, artistName));
  };

  const onArtistCardClick = (artist: ArtistModel) => {
    navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name));
  };

  const onBrowseItems = () => {
    navigate(NavigationService.getExplorePath());
  };

  const onBrowseArtists = () => {
    navigate(NavigationService.getExplorePath());
  };

  const onListingPageSelect = (page: number) => {
    setListingPage(page);
  };

  const renderPortfolioItems = () => {
    if (!portfolio) {
      return (
        <EmptyState
          header="No Portfolio Data"
          description="Portfolio data is unavailable."
          showButton={false}
        />
      );
    }

    const items = portfolio.getPortfolioDetailedItems();

    return (
      <Box my={{ sm: '24px', xl: '0px' }} textAlign={{ base: 'center', md: 'left' }}>
        <PortfolioTable
          items={items}
          isLoading={portfolioLoading}
          onItemNameClick={onItemNameClick}
          onItemArtistClick={onItemArtistClick}
          onItemEditClick={(item) => {
            // TODO: Implement edit functionality
            console.log('Edit item:', item.id);
          }}
          onItemRemovalClick={(item) => {
            // TODO: Implement removal functionality
            console.log('Remove item:', item.id);
          }}
          onBrowseClick={onBrowseItems}
        />
      </Box>
    );
  };

  const renderPortfolioStats = () => {
    if (!portfolio) {
      return (
        <EmptyState
          header="No Portfolio Data"
          description="Portfolio data is unavailable."
          showButton={false}
        />
      );
    }

    if (!portfolio.hasItems()) {
      return (
        <EmptyState
          header="No Stats"
          description="No items in portfolio to display statistics."
          showButton={false}
        />
      );
    }

    return (
      <Stack spacing={6} my={{ sm: '24px', xl: '0px' }}>
        {portfolioHistoryLoading ? (
          <Center>
            <Spinner />
          </Center>
        ) : portfolioHistory ? (
          <PortfolioHistoryChart portfolioHistoryData={portfolioHistory} showLastCalculationDate showBorder />
        ) : (
          <EmptyState
            header="No History Available"
            description="This portfolio does not yet have historical data."
            showButton={false}
          />
        )}

        {portfolioItems.length ? (
          <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="center">
            <ArtistDistributionPieChart items={portfolioItems} artists={portfolioArtists || []} />
            <TypeDistributionPieChart items={portfolioItems} />
          </Flex>
        ) : (
          <EmptyState
            header="No Item Data"
            description="Add items to the portfolio to view distribution insights."
            showButton={false}
          />
        )}
      </Stack>
    );
  };

  const renderPortfolioArtists = () => {
    if (!portfolio) {
      return (
        <EmptyState
          header="No Portfolio Data"
          description="Portfolio data is unavailable."
          showButton={false}
        />
      );
    }

    if (portfolioArtistsLoading) {
      return (
        <Center>
          <Spinner />
        </Center>
      );
    }

    if (!portfolioArtists || portfolioArtists.length === 0) {
      return (
        <EmptyState
          header="No Artists Found"
          description="No artist information is available for this portfolio."
          showButton={false}
        />
      );
    }

    return (
      <ArtistGrid
        artists={portfolioArtists}
        isLoading={portfolioArtistsLoading}
        gridDisplay
        onArtistCardClick={onArtistCardClick}
        onBrowseClick={onBrowseArtists}
      />
    );
  };

  const renderPortfolioListings = () => {
    if (!portfolio) {
      return (
        <EmptyState
          header="No Portfolio Data"
          description="Portfolio data is unavailable."
          showButton={false}
        />
      );
    }

    return (
      <Box my={{ sm: '24px', xl: '0px' }} textAlign={{ base: 'center', md: 'left' }}>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Portfolio Listings
        </Text>
        {portfolioListingsLoading && !portfolioListings ? (
          <Center>
            <Spinner />
          </Center>
        ) : portfolioListings && portfolioListings.data.length ? (
          <PaginatedListingGrid
            paginatedListings={portfolioListings}
            isLoading={portfolioListingsLoading}
            scrollMode={false}
            onPageSelect={onListingPageSelect}
            showAssociationAction={false}
          />
        ) : (
          <EmptyState
            header="No Listings"
            description="This portfolio does not have any associated listings yet."
            showButton={false}
          />
        )}
      </Box>
    );
  };

  const renderUserBanner = () => {
    if (userLoading) {
      return (
        <Box borderBottomWidth="1px" borderRadius="lg" paddingY={10}>
          <Center>
            <Spinner />
          </Center>
        </Box>
      );
    }

    if (userError || !ownerUser) {
      return (
        <EmptyState
          header="User Not Found"
          description="Unable to load user information for this portfolio."
          showButton={false}
        />
      );
    }

    return (
      <UserPortfolioBadge
        user={ownerUser}
        portfolio={portfolio}
        dataLoading={portfolioHistoryLoading || portfolioLoading}
        userLoading={userLoading}
      />
    );
  };

  if (portfolioLoading) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <Center>
          <Spinner size="xl" />
        </Center>
      </Box>
    );
  }

  if (!portfolio && !portfolioLoading) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <EmptyState
          header="Portfolio Not Found"
          description="The requested portfolio could not be found."
          showButton={false}
        />
      </Box>
    );
  }

  const tabLabels = [
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="portfolio-items-tab">
      <Icon as={IoGridOutline} />
      <Text>Items</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="portfolio-stats-tab">
      <Icon as={ImStatsDots} />
      <Text>Stats</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="portfolio-artists-tab">
      <Icon as={AiFillFormatPainter} />
      <Text>Artists</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="portfolio-sales-tab">
      <Icon as={RiAuctionLine} />
      <Text>Sales</Text>
    </Flex>
  ];

  const tabContent = [
    renderPortfolioItems(),
    renderPortfolioStats(),
    renderPortfolioArtists(),
    renderPortfolioListings()
  ];

  const showGrowthBadge = metrics.cost > 0 || metrics.value > 0;

  return (
    <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
      <Box mb={6}>
        {renderUserBanner()}
      </Box>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={8}>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Total Items</StatLabel>
          <StatNumber>{metrics.totalItems}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Unique Artists</StatLabel>
          <StatNumber>{metrics.uniqueArtists}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Market Value</StatLabel>
          <StatNumber>{toDollarFormat(metrics.value)}</StatNumber>
          {showGrowthBadge && <GrowthBadge starting={metrics.cost} ending={metrics.value} />}
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Total Cost</StatLabel>
          <StatNumber>{toDollarFormat(metrics.cost)}</StatNumber>
        </Stat>
      </SimpleGrid>

      <TabGroup
        labels={tabLabels}
        content={tabContent}
        startingTab={initialTab}
        onChange={onTabChange}
      />
    </Box>
  );
}
